import { SendHorizontal } from "lucide-react"
import * as React from "react"

import { Textarea } from "@/components/ui/textarea"
import { cn } from "@/lib/utils"

import { AuthorSide, type Message } from "../../data/review-data"

const SIDE_LABEL: Record<AuthorSide, string> = {
  [AuthorSide.CLIENT]: "Client",
  [AuthorSide.REVIEWER]: "Review team",
  [AuthorSide.AI]: "Pyxis AI",
}

const TIME_FORMAT = new Intl.DateTimeFormat(undefined, {
  month: "short",
  day: "numeric",
  hour: "numeric",
  minute: "2-digit",
})

function formatAt(iso: string): string {
  const date = new Date(iso)
  return Number.isNaN(date.getTime()) ? "" : TIME_FORMAT.format(date)
}

/**
 * One finding's conversation: the back of the checklist card.
 *
 * Everyone on the review reads the same thread — there is no private mode —
 * so the composer says who it reaches rather than offering a choice. A reader
 * who may not post (a viewer) gets the thread read-only, with no composer.
 */
export function FindingThread({
  messages,
  onSend,
  focusComposer,
}: {
  messages: readonly Message[]
  /** Omitted for a read-only reader: the messages show, the composer does not. */
  onSend?: ((body: string) => void) | undefined
  /** Move focus to the composer — when the card turns over to the thread. */
  focusComposer: boolean
}): JSX.Element {
  const [body, setBody] = React.useState("")
  const inputRef = React.useRef<HTMLTextAreaElement>(null)
  const listRef = React.useRef<HTMLDivElement>(null)
  const lastId = messages[messages.length - 1]?.id ?? null

  React.useEffect(() => {
    if (focusComposer) inputRef.current?.focus({ preventScroll: true })
  }, [focusComposer])

  React.useLayoutEffect(() => {
    const list = listRef.current
    if (list) list.scrollTop = list.scrollHeight
  }, [lastId])

  const send = (): void => {
    const trimmed = body.trim()
    if (!trimmed || !onSend) return
    onSend(trimmed)
    setBody("")
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div ref={listRef} className="min-h-0 flex-1 overflow-y-auto px-1 py-3">
        {messages.length === 0 ? (
          <p className="px-2 py-6 text-center text-caption text-text-3">
            {onSend
              ? "No messages about this finding yet. Start the conversation below."
              : "No messages about this finding yet."}
          </p>
        ) : (
          <ul className="space-y-3">
            {messages.map((m) => (
              <li key={m.id} className={cn("flex flex-col", m.mine ? "items-end" : "items-start")}>
                <div
                  className={cn(
                    "max-w-[85%] whitespace-pre-wrap break-words rounded-lg px-3 py-2 text-body-sm",
                    m.mine ? "bg-brand text-white" : "bg-surface-sunken text-text",
                  )}
                >
                  {m.body}
                </div>
                <span className="mt-1 px-1 text-[11px] text-text-3">
                  {m.mine ? "You" : SIDE_LABEL[m.authorSide]} · {formatAt(m.at)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
      {onSend ? (
        <form
          className="flex items-end gap-2 border-t border-border-soft pt-3"
          onSubmit={(event) => {
            event.preventDefault()
            send()
          }}
        >
          <div className="min-w-0 flex-1">
            <Textarea
              ref={inputRef}
              aria-label="Message"
              placeholder="Message the review team…"
              value={body}
              minRows={1}
              maxRows={5}
              maxLength={8000}
              onChange={(event) => {
                setBody(event.target.value)
              }}
              onKeyDown={(event) => {
                // Enter sends; Shift+Enter is a new line, as in every chat box.
                if (event.key === "Enter" && !event.shiftKey) {
                  event.preventDefault()
                  send()
                }
              }}
            />
          </div>
          <button
            type="submit"
            aria-label="Send message"
            disabled={body.trim().length === 0}
            className="grid size-9 shrink-0 place-items-center rounded-md bg-brand text-white transition-colors duration-120 hover:bg-brand-strong disabled:cursor-not-allowed disabled:opacity-50"
          >
            <SendHorizontal className="size-4" aria-hidden="true" />
          </button>
        </form>
      ) : (
        // Said, not just absent: a missing box reads as a broken page.
        <p className="border-t border-border-soft pt-3 text-caption text-text-3">
          You can read this conversation. Replying is for the review team and the client.
        </p>
      )}
    </div>
  )
}
